import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
// The mark over every one of this tab's empty states. One calendar for all
// four rather than a picture per tab: they are the same table read four ways,
// and four different drawings would read as four different screens.
import { ReactComponent as NoAppointmentsMark } from "../../../assets/svg/No data images/appointment.svg";
import {
  Button,
  EmptyState,
  FilterSheet,
  FloatingAction,
  Pagination,
  RowActionsMenu,
  SkeletonCard,
  TABLE_BODY_SKELETON,
  Tabs,
  failedEmptyState,
  useIsMobile,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import type { EmptyStateProps, RowAction, TabItem } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { lockedProps } from "../../billing/hooks/useSubscriptionGate";
import type {
  GateProps,
  SubscriptionGate,
} from "../../billing/hooks/useSubscriptionGate";
import { BRAND_CONFIGURATION_PATH } from "../../../routes/paths";
import { useClientAppointments } from "../hooks/useClientAppointments";
import { useMeetingApps } from "../hooks/useMeetingApps";
import AppointmentCarousel from "./AppointmentCarousel";
import AppointmentCards from "./AppointmentCards";
import AppointmentTextCell from "./AppointmentTextCell";
import CancelAppointmentModal from "./CancelAppointmentModal";
import RescheduleAppointmentModal from "./RescheduleAppointmentModal";
import ScheduleAppointmentModal from "./ScheduleAppointmentModal";
import CompleteAppointmentModal from "./CompleteAppointmentModal";
import MeetingModal from "./MeetingModal";
// The tick and cross a row wears while it is waiting on an answer. Named for
// the screen it was written for, but it is not about money: it is the pair of
// bare icon buttons the design uses wherever a table row is a question rather
// than a state, and an unpaid invoice and an unmarked appointment are the same
// question. Drawing a second pair here is how the two would drift apart.
import PaymentAnswer from "../../payments/components/PaymentAnswer";
import { agendaLabel } from "../meetingAgenda";
import {
  MEETING_NOTICE_MS,
  cancelledWithoutAppNote,
  meetingAccountNotice,
  rescheduleBlockedNotice,
} from "../meetingLinkNotices";
import {
  appointmentDate,
  appointmentShortDate,
  appointmentState,
  appointmentTime,
  durationLabel,
  inAppointmentTab,
  isUpcoming,
  upcomingSoon,
} from "../appointments";
import type {
  AppointmentState,
  AppointmentTabId,
  ClientAppointment,
  NewClientAppointment,
  RescheduleAppointment,
} from "../types/appointments.types";
// The one thing this feature takes from clients: who the appointments are
// with. Read only — the tab shows the client's name and id and changes nothing
// about the record.
import type { ClientDetail } from "../../clients/types/clients.types";

/**
 * No border on these: they sit inside a row, not beside a page heading.
 *
 * `Awaiting` has no entry because it is not drawn as a pill — a row still
 * being asked about shows the two answers instead, the way an unpaid invoice
 * does. Teal for what is coming, amber for a slot that has shifted, green for
 * a session that happened, red for one that did not.
 */
const STATUS_STYLES: Record<Exclude<AppointmentState, "Awaiting">, string> = {
  Scheduled: "bg-[#E6FAFB] text-[#007E8C]",
  Rescheduled: "bg-[#FDF3E2] text-[#9A6B1F]",
  Completed: "bg-[#E7F8EF] text-[#2F8E5B]",
  Cancelled: "bg-[#FDECEC] text-[#D14343]",
};

// The same two cells the plans and payments tables are built out of, so the
// four tabs of one screen line up rather than each having its own idea of a row.
const headCell =
  "px-3 sm:px-4 py-3 text-[13px] font-normal text-ink-muted bg-white shadow-[inset_0_-1px_0_#E9EDF1]";
const bodyCell = "px-3 sm:px-4 py-4 text-[14px] text-ink";

/**
 * All first, and the tab the table opens on. A client's appointments are read
 * as one history far more often than they are read as a filter of it, and the
 * three that follow are the three cuts anybody actually asks for.
 */
const TABLE_TABS: TabItem<AppointmentTabId>[] = [
  { id: "all", label: "All" },
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "cancelled", label: "Cancelled" },
];

/**
 * The same four, as rows for the phone's filter sheet — built once, since the
 * tabs themselves never change. A phone has no strip to put them in: four tabs
 * beside a heading scroll sideways behind a gesture nothing announces, so they
 * are folded into the sheet the revenue screen narrows itself with. See
 * `FilterSheet`.
 */
const TAB_OPTIONS = TABLE_TABS.map((tab) => ({ value: tab.id, label: tab.label }));

/**
 * A menu entry that answers for itself when the workspace is shut.
 *
 * `RowActionsMenu` greys a disabled entry out but still lets it be chosen, for
 * exactly this — see its `disabled`. So a locked entry selects into the gate's
 * own explanation rather than into nothing, which is the same courtesy every
 * gated button on the screen extends.
 */
const gatedAction = (
  label: string,
  gate: GateProps,
  onSelect: () => void
): RowAction => ({
  label,
  disabled: gate.disabled,
  onSelect: gate.disabled ? gate.onDisabledClick ?? (() => {}) : onSelect,
});

/**
 * Rows to a page. Ten, like every other table in the app — the plan list, the
 * client roster, the revenue history — so a practitioner moving between them
 * is not re-learning how far a page goes each time.
 */
const APPOINTMENTS_PER_PAGE = 10;

/** The one thing a dialog is open about, and which dialog it is. */
type Pending =
  | { kind: "schedule" }
  | { kind: "meeting"; appointment: ClientAppointment }
  | { kind: "reschedule"; appointment: ClientAppointment }
  | { kind: "cancel"; appointment: ClientAppointment }
  | { kind: "complete"; appointment: ClientAppointment };

export interface ClientAppointmentsTabProps {
  client: ClientDetail;
  /**
   * Whether this workspace may still change anything, read once by the screen
   * above — see `ClientDetailPage`. Booking a consultation, moving one and
   * calling one off are all plainly things a subscription pays for. The
   * history stays readable either way, and every appointment already in it
   * stands.
   */
  gate: SubscriptionGate;
  /** The client's first name, for the sentence the cancel dialog writes. */
  firstName: string;
}

/**
 * Everything booked with this client: the week ahead as a card to page
 * through, and the whole history as a table under it.
 *
 * The two are the same list read at two distances. The carousel is a reminder
 * of what is coming and carries the two things somebody would do about it —
 * move it, or call it off. The table is the record: every appointment ever
 * booked, cut four ways, with the same two acts on the row's own menu.
 */
const ClientAppointmentsTab: React.FC<ClientAppointmentsTabProps> = ({
  client,
  gate,
  firstName,
}) => {
  const toast = useToast();
  // Two readers of one cached answer. `anyConnected` is the first-run state's
  // third line: while no meeting app is connected, connecting one is a thing
  // worth naming beside what the tab buys, and once one is there is nothing
  // to offer and the line goes. The whole state goes to the notices the
  // reschedule and cancel handlers raise, which have to name the app that was
  // switched off — see `meetingLinkNotices`.
  const meetingApps = useMeetingApps();
  const { anyConnected } = meetingApps;
  const {
    appointments,
    status: loadStatus,
    error,
    schedule,
    reschedule,
    cancel,
    complete,
  } = useClientAppointments(client.id);

  // A history that lands quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(loadStatus === "loading");

  // The two answers every row's menu wears, worked out once rather than per
  // row: they do not differ between rows, and a table of fifty would otherwise
  // build a hundred identical objects on every render.
  const rescheduleGate = gate.props("reschedule an appointment");
  const cancelGate = gate.props("cancel an appointment");
  // Every row still being asked about wears the same pair, so it is worked out
  // once rather than per row.
  const answerGate = gate.props("mark an appointment");
  // The Meeting dialog offers both acts behind one gate, so it is worded for
  // both rather than for either — the same phrase My task passes it.
  const changeGate = gate.props("change an appointment");
  // Worn by three controls that are never on screen together: the button in
  // the card head over `md`, the floating pill under it, and the empty table's
  // own offer. An empty table is not a reason to let an unsubscribed workspace
  // book something, so all three are locked by the same words.
  const scheduleGate = gate.props("schedule an appointment");

  /**
   * Which shape the history and its four cuts take.
   *
   * Not a `md:` class: a table of eight columns and a column of cards are not
   * the same markup at two widths, and building both would put every
   * appointment in the accessibility tree twice — along with two live ticks,
   * two live crosses and two menus per row. The tabs go the same way: a strip
   * and a folded sheet are two different controls, not one with different
   * padding. See `AppointmentCards` and `FilterSheet`.
   */
  const isMobile = useIsMobile();

  const [tab, setTab] = useState<AppointmentTabId>("all");
  // Where the table is, from `md` up. Reset whenever the cut changes — see
  // `handleTabChange`.
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<Pending | null>(null);
  // Set while a write is with the server, so the dialog's action spins rather
  // than taking a second press and booking the appointment twice.
  const [saving, setSaving] = useState(false);

  /**
   * One `now` for the whole tab, so the carousel and the table cannot disagree
   * about whether an appointment has started.
   *
   * Held rather than read per render, for two reasons. A fresh `Date` on every
   * render is a new dependency on every render, which would rebuild both memos
   * below whenever anything at all changed. And it has to move on its own: an
   * appointment at eleven has to leave the carousel and land in Past without
   * anybody reloading, and a screen left open across it would otherwise sit
   * there offering to join a call that finished an hour ago.
   *
   * Every minute, which is the resolution these are booked at.
   */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const soon = useMemo(
    () => upcomingSoon(appointments, now),
    [appointments, now]
  );

  /**
   * Whether the Link/Address column is drawn at all.
   *
   * Dropped on Past and Cancelled, where a link is a way in to something that
   * has already been and gone — and where Cancelled has a Reason to put in the
   * space instead, which is the one thing those rows have to say. The space
   * goes to the columns that still say something.
   */
  const showLink = tab !== "past" && tab !== "cancelled";

  /** Why each row was called off — only ever asked on the tab about that. */
  const showReason = tab === "cancelled";

  /**
   * What the practitioner wrote down about each session — the same column on
   * the tab about sessions that have been.
   *
   * Past rather than every tab, for the reason Reason is only on Cancelled: a
   * note is written when an appointment is marked, so it is a thing only a row
   * behind us can have, and a column that is empty for four rows in five is a
   * column that has taken the width of one that is not.
   */
  const showNote = tab === "past";

  /**
   * What each column is worth, in the order they are drawn: Sl, Date & time,
   * Agenda, Type, Duration, then whichever of Link/Address and Reason this tab
   * has, then Status and the row menu.
   *
   * A set per shape rather than one set with the odd column swapped out.
   * Dropping a column and leaving the rest alone would hand its whole share to
   * Status — a pill two words wide floating in a quarter of the table, with a
   * gulf between it and the Duration beside it. So the freed width goes back
   * to the columns that have something to put in it.
   *
   * Each share is sized to its longest ordinary answer rather than split
   * evenly. Sl holds two digits and takes the least of anything here; Type and
   * Duration hold one short phrase each ("Offline", "1 hr 30 min") and are
   * held to what that needs. Agenda is a word or two off a list of seven, the
   * longest of which is "Initial consultation", and it was paid for out of
   * Type and Duration rather than out of the two prose columns: both of those
   * were already at the width where an address starts clipping, and a column
   * that reads "Offline" cannot use the 12% it used to hold. What they give up goes to the two columns that
   * hold prose and are the ones that actually run out of room: Link/Address,
   * where an address is a building and a street clipped to one line, and
   * Reason on the Cancelled tab. At the table's 900px floor that is around
   * 210px of address before it clips, against 120px before this.
   */
  const columnWidths =
    showReason || showNote
      ? ["w-[5%]", "w-[17%]", "w-[14%]", "w-[9%]", "w-[10%]", "w-[26%]", "w-[12%]", "w-[7%]"]
      : ["w-[5%]", "w-[18%]", "w-[14%]", "w-[9%]", "w-[10%]", "w-[22%]", "w-[15%]", "w-[7%]"];

  /** What the empty state has to stretch across. */
  const columnCount = columnWidths.length;

  const matching = useMemo(
    () =>
      appointments.filter((appointment) =>
        inAppointmentTab(appointment, tab, now)
      ),
    [appointments, tab, now]
  );

  /**
   * The page being looked at, and the rows on it.
   *
   * `currentPage` is clamped rather than stored clamped, because the list
   * moves under it: cancelling the last row of the last page of Upcoming
   * leaves `page` pointing past the end, and a table that answered with
   * nothing would read as a tab that had emptied. Clamping lands them on the
   * new last page instead.
   *
   * Paged here rather than by an endpoint, for the same reason the payment
   * history is: the whole list is already in hand, and the carousel above and
   * the four tabs beside it all read from it. It moves to the server the day
   * the endpoint can filter by state for itself.
   */
  const totalPages = Math.max(
    1,
    Math.ceil(matching.length / APPOINTMENTS_PER_PAGE)
  );
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * APPOINTMENTS_PER_PAGE;
  /**
   * A page of the table, or the whole cut on a phone, where walking the list
   * is scrolling and there are no pages under it.
   *
   * Handed over whole rather than in batches, unlike the revenue list: that
   * one is the practice's entire ledger arriving a page at a time from the
   * server, and this is every appointment ever booked with one person — in
   * hand already, and a list a phone lays out in one go.
   */
  const visible = isMobile
    ? matching
    : matching.slice(startIndex, startIndex + APPOINTMENTS_PER_PAGE);

  /**
   * A narrower cut is a different list, so it is read from the top — whichever
   * of the two controls asked for it. Page three of All is rarely page three
   * of Cancelled and is often not a page at all, and a table that answered
   * with nothing would read as the cut itself being empty.
   */
  const handleTabChange = (next: AppointmentTabId) => {
    setTab(next);
    setPage(1);
  };

  /** Every write fails the same way, and the dialog stays up to be tried again. */
  const report = (title: string, cause: unknown) => {
    // One failure is not described as "could not reschedule this
    // appointment": a meeting made on another account is refused whatever was
    // being done to it, and what the reader needs is the account and the tab,
    // not the act. It gets its own words and the longer stand to read them
    // in — see `meetingAccountNotice`.
    const mismatch = meetingAccountNotice(cause);
    if (mismatch) {
      toast.error(mismatch.title, mismatch.message, MEETING_NOTICE_MS);
      console.error(`${title.toLowerCase()}:`, cause);
      return;
    }

    toast.error(
      title,
      cause instanceof ApiError
        ? cause.message
        : "We could not reach the server. Check your connection and try again."
    );
    console.error(`${title.toLowerCase()}:`, cause);
  };

  const handleSchedule = async (input: NewClientAppointment) => {
    if (saving) return;
    setSaving(true);

    try {
      const booked = await schedule(input);
      setPending(null);
      toast.success(
        "Appointment scheduled",
        `${appointmentShortDate(booked.startsAt)} at ${appointmentTime(
          booked.startsAt
        )} with ${firstName}.`
      );
    } catch (cause) {
      report("Could not schedule this appointment", cause);
    } finally {
      setSaving(false);
    }
  };

  const handleReschedule = async (input: RescheduleAppointment) => {
    if (saving || pending?.kind !== "reschedule") return;

    // An online appointment cannot be moved while nothing is making meeting
    // links: the new slot would need a new link and there is no app left to
    // make one. Said here rather than left to the backend, which can only
    // report the failure after the fact and in its own words. The dialog
    // stays up, because reconnecting the app is what makes the move possible
    // and the slot that was picked is still the slot that was wanted.
    const blocked = rescheduleBlockedNotice(
      pending.appointment.type,
      meetingApps
    );
    if (blocked) {
      // Longer than a confirmation's stand — the message is an instruction,
      // and it names a tab to go to. See `MEETING_NOTICE_MS`.
      toast.error(blocked.title, blocked.message, MEETING_NOTICE_MS);
      return;
    }

    setSaving(true);

    try {
      const moved = await reschedule(pending.appointment.id, input);
      setPending(null);
      toast.success(
        "Appointment rescheduled",
        `Moved to ${appointmentShortDate(moved.startsAt)} at ${appointmentTime(
          moved.startsAt
        )}.`
      );
    } catch (cause) {
      report("Could not reschedule this appointment", cause);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (saving || pending?.kind !== "cancel") return;
    const { appointment } = pending;
    setSaving(true);

    try {
      await cancel(appointment.id, reason);
      setPending(null);
      // Calling one off needs no working link, so it goes through either way —
      // but on a workspace that has disconnected its app nobody withdrew the
      // invitation, and that is a job left for the practitioner. It is said on
      // the end of the confirmation rather than as a second card.
      const note = cancelledWithoutAppNote(appointment.type, meetingApps);
      toast.success(
        "Appointment cancelled",
        `The ${appointmentShortDate(
          appointment.startsAt
        )} appointment with ${firstName} has been called off.${
          note ? ` ${note}` : ""
        }`,
        // Only the longer stand where the extra line is there to be read.
        note ? MEETING_NOTICE_MS : undefined
      );
    } catch (cause) {
      report("Could not cancel this appointment", cause);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (note: string) => {
    if (saving || pending?.kind !== "complete") return;
    const { appointment } = pending;
    setSaving(true);

    try {
      await complete(appointment.id, note);
      setPending(null);
      toast.success(
        "Appointment completed",
        `The ${appointmentShortDate(
          appointment.startsAt
        )} session with ${firstName} is on their record.`
      );
    } catch (cause) {
      report("Could not mark this appointment completed", cause);
    } finally {
      setSaving(false);
    }
  };

  /**
   * What a narrowed tab says when nothing in it matched.
   *
   * Compact, and in their own words — "no appointments" under Cancelled would
   * say this client has never been seen, when what it means is that nothing
   * has been called off. Two of the three are good news as much as they are a
   * gap, and are worded as news.
   *
   * Written the way the revenue screen writes its tabs, and for the same
   * reason: these are a list reporting a result, not a screen nobody has used
   * yet. The welcome mat below belongs to All, where an empty table really
   * does mean this client has never been booked — see `EmptyState`'s note on
   * when each layout is right. Offering the same illustration and the same
   * "what appointments buy you" list on all four would say that four times
   * over to somebody who has appointments and only wanted to know what is
   * coming up.
   *
   * No action on these three. The card's own Schedule button is a few lines
   * up and always there, and the revenue tabs carry none either.
   */
  const NARROWED_EMPTY: Record<
    Exclude<AppointmentTabId, "all">,
    EmptyStateProps
  > = {
    upcoming: {
      title: "No appointments coming up",
      description: `Nothing is booked with ${client.name} from here on. One you schedule waits here until it has been.`,
    },
    past: {
      title: "No appointments behind you yet",
      description: `Once an appointment with ${client.name} has been and gone, it is kept here with its date and length.`,
    },
    cancelled: {
      title: "No appointments cancelled",
      description: `No appointment with ${client.name} has been called off. Cancelled ones stay listed here for your records.`,
    },
  };

  /**
   * What the table says when it has no rows to draw.
   *
   * A failure comes before emptiness: an empty table under "no appointments
   * yet" would read as an answer, when nothing was ever heard back. It keeps
   * the compact layout too — a welcome mat under an error reads as though the
   * error were the screen anybody meant to land on.
   */
  const emptyState = (): EmptyStateProps => {
    if (loadStatus === "failed") {
      const state = failedEmptyState(
        "We could not load this client’s appointments"
      );
      return error ? { ...state, description: error } : state;
    }

    if (tab !== "all") return NARROWED_EMPTY[tab];

    // The one first-run state on the tab: no appointment has ever been booked
    // with this client, so the empty table is not a result — it is the first
    // thing anybody sees here, and it says what the tab is for before asking
    // to fill it.
    return {
      variant: "welcome",
      illustration: <NoAppointmentsMark aria-hidden className="h-14 w-14" />,
      title: "Schedule the appointment here",
      description: `You haven’t scheduled any appointments with ${client.name}. Schedule one to share a date, time and meeting link.`,
      highlights: {
        title: "With appointment, you can",
        items: [
          "Give clients a clear date and time to connect.",
          "Share a meeting link so they can join easily.",
          // Dropped rather than reworded once an app is connected: the link
          // is already being made, and a line still offering to set that up
          // reads as a step somebody missed.
          ...(anyConnected
            ? []
            : [
                <>
                  Auto-create the{" "}
                  {/* Followed in place, like the same offer in the Schedule
                      dialog: a tab of this app is not a new tab, and there is
                      nothing on this screen to lose — the table it stands in
                      is empty. */}
                  <Link
                    to={BRAND_CONFIGURATION_PATH}
                    className="font-medium text-brand hover:underline"
                  >
                    Meet link
                  </Link>{" "}
                  by connecting your account
                </>,
              ]),
        ],
      },
      action: (
        // A step up from the `sm` button in the card head, because there it
        // sits beside a heading and here it is the only thing on the screen
        // worth pressing. Locked the same way that one is: an empty table is
        // not a reason to let an unsubscribed workspace book something.
        <Button
          size="md"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={() => setPending({ kind: "schedule" })}
          {...scheduleGate}
        >
          Schedule appointment
        </Button>
      ),
    };
  };

  return (
    // The air at the foot is for the floating New schedule on a phone: without
    // it the last appointment of a long history ends underneath the pill.
    <div className="flex flex-col gap-4 min-h-0 flex-1 max-md:pb-20">
      <AppointmentCarousel
        appointments={soon}
        onReschedule={(appointment) =>
          setPending({ kind: "reschedule", appointment })
        }
        onCancel={(appointment) => setPending({ kind: "cancel", appointment })}
      />

      {/* One card holding the lot from `md` up. On a phone there is no card:
          the rows are cards of their own, and a border around a column of
          bordered cards is a box inside a box that costs a phone's width twice
          over — see `AppointmentCards`. */}
      <section
        className={`flex-1 min-h-0 flex flex-col overflow-hidden ${
          isMobile ? "" : "rounded-md border border-line bg-white"
        }`}
      >
        <div
          className={`flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
            isMobile ? "pb-3" : "px-5 sm:px-6 pt-5 pb-4"
          }`}
        >
          {/* The heading and, on a phone, the four cuts behind one word at the
              end of its line. A row rather than the heading alone so the two
              sit at opposite edges when the heading has the screen's width to
              itself; from `sm` up it shrinks back to the heading and the
              wrapper is just where it stands. */}
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-[16px] 3xl:text-[18px] font-medium text-ink">
              Total appointment
            </h2>
            {isMobile && (
              <FilterSheet
                groups={[
                  {
                    label: "Status",
                    options: TAB_OPTIONS,
                    value: tab,
                    onChange: (next) => handleTabChange(next as AppointmentTabId),
                    defaultValue: "all",
                  },
                ]}
              />
            )}
          </div>
          {/* Beside the heading from `md` up, and nowhere on a phone: down
              there it is the floating pill at the end of this tab instead.
              This row is read once and then scrolled past — a booking is
              wanted after the history under it has been read, by which time
              the heading and its button are off the top of the screen. */}
          {!isMobile && (
            <Button
              size="sm"
              startIcon={<Plus className="w-4 h-4" />}
              onClick={() => setPending({ kind: "schedule" })}
              {...scheduleGate}
            >
              Schedule
            </Button>
          )}
        </div>

        {/* The strip the four cuts are pressed from, from `md` up. A phone has
            them in the sheet above instead — rendered rather than hidden, so
            the same four are not two sets of controls over one table. */}
        {!isMobile && (
          <div className="flex-shrink-0 border-b border-line px-5 sm:px-6">
            <Tabs tabs={TABLE_TABS} activeTab={tab} onChange={handleTabChange} />
          </div>
        )}

        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: this line is not seen, so it costs nothing to be
            early, and the skeleton it stands for is hidden from screen readers
            — it is the shape of an answer rather than the answer. */}
        {loadStatus === "loading" && (
          <p role="status" className="sr-only">
            Loading appointments…
          </p>
        )}

        {/* Rendered rather than hidden: a table and a column of cards holding
            the same appointments would be the same bookings twice over in the
            accessibility tree, and the cards inside the hidden half would still
            be reachable by keyboard.

            The phone has no pages under it — walking the history there is
            scrolling — so the cards are handed the whole cut and the table
            keeps its pagination. */}
        {isMobile ? (
          <AppointmentCards
            appointments={visible}
            phase={phase}
            empty={emptyState()}
            now={now}
            statusStyles={STATUS_STYLES}
            answerGate={answerGate}
            rescheduleGate={rescheduleGate}
            cancelGate={cancelGate}
            onOpen={(appointment) =>
              setPending({ kind: "meeting", appointment })
            }
            onReschedule={(appointment) =>
              setPending({ kind: "reschedule", appointment })
            }
            onCancel={(appointment) =>
              setPending({ kind: "cancel", appointment })
            }
            onComplete={(appointment) =>
              setPending({ kind: "complete", appointment })
            }
          />
        ) : phase !== "ready" && visible.length === 0 ? (
          // One block the size of the table, rather than a page of blocks in
          // the shape of its cells — see `TABLE_BODY_SKELETON`.
          <div className="flex-1 min-h-0 p-4 sm:p-6">
            <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
            {/* Full height only when the rows are gone — see `ClientsTable`. */}
            <table
              className={`w-full min-w-[900px] table-fixed border-collapse text-left ${
                visible.length === 0 ? "h-full" : ""
              }`}
            >
              {/* Three sets of widths rather than one with holes in it: a
                  column that is not drawn is not a column, and leaving its
                  share behind would open a gap in the middle of the row. */}
              <colgroup>
                {columnWidths.map((width, column) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <col key={column} className={width} />
                ))}
              </colgroup>
              <thead className="sticky top-0 z-10">
                <tr>
                  <th scope="col" className={headCell}>
                    Sl
                  </th>
                  <th scope="col" className={headCell}>
                    Date &amp; time
                  </th>
                  {/* On every tab, unlike the two columns below it. What an
                      appointment was for is as much a fact about one that has
                      been and gone as about one still ahead — a history read
                      down the Past tab without it is a column of hours with no
                      account of what any of them were. */}
                  <th scope="col" className={headCell}>
                    Agenda
                  </th>
                  <th scope="col" className={headCell}>
                    Type
                  </th>
                  <th scope="col" className={headCell}>
                    Duration
                  </th>
                  {showLink && (
                    <th scope="col" className={headCell}>
                      Link/Address
                    </th>
                  )}
                  {showReason && (
                    <th scope="col" className={headCell}>
                      Reason
                    </th>
                  )}
                  {showNote && (
                    <th scope="col" className={headCell}>
                      Note
                    </th>
                  )}
                  <th scope="col" className={headCell}>
                    Status
                  </th>
                  {/* Named for a screen reader, which cannot see that the
                      column is a row of kebabs rather than a heading. */}
                  <th scope="col" className={headCell}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="align-middle">
                      <EmptyState {...emptyState()} />
                    </td>
                  </tr>
                ) : (
                  visible.map((appointment, index) => {
                    const state = appointmentState(appointment, now);
                    // Still going to happen — which is what both of the
                    // things this row offers depend on. Asked of the record
                    // rather than of the pill, which also says "Awaiting" for
                    // a booking whose time has passed. See `isUpcoming`.
                    const ahead = isUpcoming(appointment, now);
                    // A link worth pressing, which a finished or cancelled
                    // appointment's is not: the call is over or was never
                    // going to happen, and offering a way into it invites
                    // somebody to sit in an empty room. Per row rather than
                    // per tab, because All holds all three kinds at once.
                    const joinable =
                      ahead &&
                      appointment.type === "Online" &&
                      !!appointment.meetingUrl;
                    // Only a standing appointment still ahead has anything to
                    // do to it. Moving one that has already happened would be
                    // rewriting history, and a cancelled one is not revived by
                    // giving it a new time — so those rows carry no menu
                    // rather than a menu of entries that cannot be meant.
                    const actionable = ahead;

                    return (
                      <tr
                        key={appointment.id}
                        // The whole row opens the appointment, the way a row
                        // of the client roster opens the client. A booking is
                        // a record in its own right — it has an agenda, a
                        // paragraph about what it is for, who it is with and
                        // where — and a table of seven columns can show about
                        // half of that. The press is what the rest is behind,
                        // and it is the same dialog My task opens.
                        onClick={() =>
                          setPending({ kind: "meeting", appointment })
                        }
                        className="border-b border-line last:border-0 hover:bg-[#F8FEFF] transition-colors cursor-pointer"
                      >
                        {/* Numbered through the tab rather than from one on
                            every page: page two opening at 01 again would make
                            two different rows both "row 1". */}
                        <td className={bodyCell}>
                          {String(startIndex + index + 1).padStart(2, "0")}
                        </td>
                        {/* The row is clickable for a pointer; this is the
                            real button that keeps the table reachable from a
                            keyboard, the way the client roster's name cell is.
                            The date rather than a cell of its own, because it
                            is what the row is called. */}
                        <td className={bodyCell}>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setPending({ kind: "meeting", appointment });
                            }}
                            className="rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                          >
                            <span className="block hover:underline">
                              {appointmentDate(appointment.startsAt)}
                            </span>
                            <span className="block text-[13px] text-ink-muted">
                              {appointmentTime(appointment.startsAt)}
                            </span>
                            <span className="sr-only">
                              {" "}
                              — open this appointment
                            </span>
                          </button>
                        </td>
                        {/* Clipped to the row with the rest on hover, like
                            the other prose cells — "Initial consultation" is
                            the one entry that can outrun the column. A dash
                            where none was chosen: the booking dialog asks but
                            does not insist. */}
                        <td className={bodyCell}>
                          <AppointmentTextCell
                            text={agendaLabel(appointment.agenda)}
                            heading="Agenda"
                          />
                        </td>
                        <td className={bodyCell}>{appointment.type}</td>
                        <td className={bodyCell}>
                          {durationLabel(appointment.durationMinutes)}
                        </td>
                        {/* One column, two answers: the way in to an online
                            appointment, or where to turn up for an offline
                            one. A row has at most one of them, and a dash
                            rather than a blank cell where it has neither — an
                            empty cell reads as one that failed to load.

                            The address is shown whether or not the
                            appointment is still ahead, where the link is not:
                            a link is an invitation, and offering a way into a
                            call that is over invites somebody to sit in an
                            empty room. An address is a fact about where the
                            sitting was, and stays worth reading afterwards. */}
                        {showLink && (
                        // Stopped here rather than on the link alone, so the
                        // cell's whole hit area belongs to whatever is in it:
                        // a press that lands a pixel beside the Join should do
                        // nothing, not open a dialog over the tab somebody was
                        // about to leave for.
                        <td
                          className={bodyCell}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {joinable ? (
                            <a
                              href={appointment.meetingUrl!}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 font-medium text-[#00A5B4] hover:text-[#007E8C] hover:underline transition-colors"
                            >
                              <span
                                aria-hidden="true"
                                className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#00BACB]"
                              />
                              Join link
                              <span className="sr-only">
                                {" "}
                                for the{" "}
                                {appointmentDate(appointment.startsAt)}{" "}
                                appointment
                              </span>
                            </a>
                          ) : (
                            <AppointmentTextCell
                              text={
                                appointment.type === "Offline"
                                  ? appointment.address
                                  : null
                              }
                              heading="Address"
                            />
                          )}
                        </td>
                        )}
                        {/* Clipped to the row's one line, with the rest on
                            hover — see `AppointmentTextCell`. */}
                        {showReason && (
                          <td className={bodyCell}>
                            <AppointmentTextCell
                              text={appointment.cancelReason}
                              heading="Reason"
                            />
                          </td>
                        )}
                        {/* What was written down when the session was marked.
                            Empty on a row still waiting to be answered and on
                            one that was marked without a note, which the cell
                            draws as a dash rather than as a blank. */}
                        {showNote && (
                          <td className={bodyCell}>
                            <AppointmentTextCell
                              text={appointment.completionNote}
                              heading="Note"
                            />
                          </td>
                        )}
                        {/* A row whose time has passed with nobody saying
                            what became of it is not a state to report so much
                            as a question to answer, so its cell is the two
                            answers rather than a pill. The same arrangement
                            the payment history makes of an unpaid row, and the
                            same control — see `PaymentAnswer`. */}
                        <td
                          className={bodyCell}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {state === "Awaiting" ? (
                            <div className="flex items-center gap-2">
                              {/* Said for a screen reader, which cannot see
                                  that the cell holds a tick and a cross
                                  rather than a word. */}
                              <span className="sr-only">
                                Waiting to be marked
                              </span>
                              <PaymentAnswer
                                tone="confirm"
                                label={`Mark the ${appointmentDate(
                                  appointment.startsAt
                                )} appointment as completed`}
                                gate={answerGate}
                                onClick={() =>
                                  setPending({ kind: "complete", appointment })
                                }
                              />
                              {/* The cross is the same act as calling off an
                                  appointment still ahead, and goes to the same
                                  dialog: the backend keeps one state for a
                                  session that did not take place however it
                                  failed to. */}
                              <PaymentAnswer
                                tone="cancel"
                                label={`Cancel the ${appointmentDate(
                                  appointment.startsAt
                                )} appointment — the session did not take place`}
                                gate={answerGate}
                                onClick={() =>
                                  setPending({ kind: "cancel", appointment })
                                }
                              />
                            </div>
                          ) : (
                            <span
                              className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${STATUS_STYLES[state]}`}
                            >
                              {state}
                            </span>
                          )}
                        </td>
                        <td
                          className={`${bodyCell} text-right`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {actionable && (
                            <RowActionsMenu
                              label={`Actions for the ${appointmentDate(
                                appointment.startsAt
                              )} appointment`}
                              actions={[
                                gatedAction(
                                  "Reschedule",
                                  rescheduleGate,
                                  () =>
                                    setPending({
                                      kind: "reschedule",
                                      appointment,
                                    })
                                ),
                                {
                                  ...gatedAction("Cancel", cancelGate, () =>
                                    setPending({ kind: "cancel", appointment })
                                  ),
                                  destructive: true,
                                },
                              ]}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Outside the scrolling body, so the pages stay put while the rows
            move under them — and drawn whatever the table is holding, so the
            foot of the card does not appear and vanish as tabs are pressed.
            `resultCount` is what is on this page, which is what `Pagination`
            writes as "Result N".

            Nothing to draw on a phone: the cards are the whole cut, and a
            pager under a list that is already all of itself is a control with
            one page to offer. */}
        {!isMobile && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
            resultCount={visible.length}
          />
        )}
      </section>

      {/* The way to book, on a phone: the app's floating pill, the same one My
          task raises a booking from — see `FloatingAction`. After the table in
          the tab order, since it floats over the end of it, and drawn only
          under `md` because the card's own head keeps the labelled button
          above that width. */}
      {isMobile && (
        <FloatingAction
          label="New schedule"
          // Marked disabled to a reader and turned back by hand rather than
          // natively disabled, so the press that did not know why is answered
          // — see `lockedProps`.
          {...lockedProps(scheduleGate, () => setPending({ kind: "schedule" }))}
        />
      )}

      {/* Only ever one of the three. Each stays up while its write is with the
          server, so a booking that could not be made leaves the details on
          screen to send again rather than closing over a failure. */}
      {pending?.kind === "schedule" && (
        <ScheduleAppointmentModal
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleSchedule}
        />
      )}

      {/* One appointment, read whole — the same dialog My task opens on a row
          of its timeline, and the reason it lives in this feature rather than
          in that one. Its Reschedule and Cancel hand straight back to the two
          dialogs below, against the record as the dialog last read it rather
          than as the table last listed it. */}
      {pending?.kind === "meeting" && (
        <MeetingModal
          appointment={pending.appointment}
          gate={changeGate}
          onClose={() => setPending(null)}
          onReschedule={(appointment) =>
            setPending({ kind: "reschedule", appointment })
          }
          onCancel={(appointment) => setPending({ kind: "cancel", appointment })}
        />
      )}

      {pending?.kind === "reschedule" && (
        <RescheduleAppointmentModal
          appointment={pending.appointment}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleReschedule}
        />
      )}

      {pending?.kind === "complete" && (
        <CompleteAppointmentModal
          appointment={pending.appointment}
          firstName={firstName}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleComplete}
        />
      )}

      {pending?.kind === "cancel" && (
        <CancelAppointmentModal
          appointment={pending.appointment}
          firstName={firstName}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleCancel}
        />
      )}
    </div>
  );
};

export default ClientAppointmentsTab;
